import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

// Reusable lead form. `fields` controls which inputs show.
export function LeadForm({
  source,
  program = "",
  fields = ["name", "email", "phone", "message"],
  categories = null,
  programs = null,
  onStarted,
  onCompleted,
  submitLabel = "Gönder",
  testid = "lead-form",
}) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", organization: "", role: "", city: "",
    category: "", program, message: "", kvkk: false, hp: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [touched, setTouched] = useState(false);

  const set = (k, v) => {
    if (!touched) { setTouched(true); onStarted && onStarted(); }
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.kvkk) { toast.error("Lütfen KVKK onayını işaretleyin."); return; }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source, program: form.program || program });
      onCompleted && onCompleted();
      setDone(true);
      toast.success("Başvurun alındı. En kısa sürede iletişime geçeceğiz.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid={`${testid}-success`} className="bg-brand-cream border border-brand-gold-light/50 rounded-sm p-10 text-center">
        <CheckCircle2 className="mx-auto text-brand-gold" size={48} />
        <h3 className="font-serif text-3xl text-brand-green mt-4">Teşekkürler!</h3>
        <p className="text-brand-text/70 mt-2">Başvurun bize ulaştı. Ekibimiz en kısa sürede seninle iletişime geçecek.</p>
      </div>
    );
  }

  const inputCls = "w-full px-4 py-3 text-sm rounded-sm border border-brand-gold-light/50 bg-white text-brand-text outline-none focus:border-brand-gold";

  return (
    <form onSubmit={submit} data-testid={testid} className="space-y-4">
      <input type="text" name="hp" value={form.hp} onChange={(e) => set("hp", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden />

      {fields.includes("name") && (
        <div>
          <label className="block text-sm font-medium text-brand-text mb-1.5">Ad Soyad</label>
          <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid={`${testid}-name`} />
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.includes("email") && (
          <div>
            <label className="block text-sm font-medium text-brand-text mb-1.5">E-posta</label>
            <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid={`${testid}-email`} />
          </div>
        )}
        {fields.includes("phone") && (
          <div>
            <label className="block text-sm font-medium text-brand-text mb-1.5">Telefon</label>
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid={`${testid}-phone`} />
          </div>
        )}
      </div>

      {fields.includes("organization") && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brand-text mb-1.5">Kurum</label>
            <input value={form.organization} onChange={(e) => set("organization", e.target.value)} className={inputCls} data-testid={`${testid}-organization`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-text mb-1.5">Görev</label>
            <input value={form.role} onChange={(e) => set("role", e.target.value)} className={inputCls} data-testid={`${testid}-role`} />
          </div>
        </div>
      )}

      {fields.includes("city") && (
        <div>
          <label className="block text-sm font-medium text-brand-text mb-1.5">Şehir</label>
          <input value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} data-testid={`${testid}-city`} />
        </div>
      )}

      {fields.includes("category") && categories && (
        <div>
          <label className="block text-sm font-medium text-brand-text mb-1.5">Kategori</label>
          <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid={`${testid}-category`}>
            <option value="">Seçiniz</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}

      {fields.includes("program") && programs && (
        <div>
          <label className="block text-sm font-medium text-brand-text mb-1.5">İlgilenilen Program</label>
          <select value={form.program} onChange={(e) => set("program", e.target.value)} className={inputCls} data-testid={`${testid}-program`}>
            <option value="">Seçiniz</option>
            {programs.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      )}

      {fields.includes("message") && (
        <div>
          <label className="block text-sm font-medium text-brand-text mb-1.5">Mesaj</label>
          <textarea rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls} data-testid={`${testid}-message`} />
        </div>
      )}

      <label className="flex items-start gap-2 text-xs text-brand-text/70">
        <input type="checkbox" checked={form.kvkk} onChange={(e) => set("kvkk", e.target.checked)} className="mt-0.5 accent-brand-gold" data-testid={`${testid}-kvkk`} />
        <span>Kişisel verilerimin bu talep kapsamında işlenmesine (KVKK) onay veriyorum.</span>
      </label>

      <button type="submit" disabled={loading} data-testid={`${testid}-submit`} className="w-full sm:w-auto bg-brand-gold text-brand-offwhite hover:bg-brand-green transition-colors px-10 py-3.5 text-sm font-semibold rounded-sm disabled:opacity-60">
        {loading ? "Gönderiliyor..." : submitLabel}
      </button>
    </form>
  );
}
